import React from 'react';
import { useAppDispatch } from 'hooks';
import { Button } from 'reactstrap';
import { useFormikContext } from 'formik';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Modal } from '~components';
import { Form as WizardForm } from '~components/wizard-form';

import { BulkSignupFilters, MandateFilter, resolveMandateStates } from '~direct-debits/models';

import steps, { DEFAULT_BODY, DEFAULT_SUBJECT } from '~direct-debits/forms/bulkSignup';

import { clientSignupsApi } from '~direct-debits/actions/clientSignups';
import { useBulkSendMutation } from '~direct-debits/actions/signups';

interface ComponentProps {
  isOpen: boolean;
  setIsOpen: (toggle: boolean) => void;
}

interface FormValues {
  mandate_filter: MandateFilter;
  has_outstanding_invoice?: boolean;
  has_email?: boolean;
  last_sent_days?: number;
  subject: string;
  body: string;
  preview?: { count: number; filters: BulkSignupFilters };
  preview_panel?: unknown;
}

/**
 * Reads the recipient count from Formik context so the submit button can
 * display "Send X Emails" as required by the AC. Sourced from Step 1's
 * debounced preview count — the single source of truth — rather than
 * re-fetching in Step 3.
 */
const SubmitButton: React.FC = () => {
  const { values, isSubmitting, submitForm } = useFormikContext<FormValues>();
  const count = values.preview?.count ?? 0;
  return (
    <Button
      color="success"
      disabled={isSubmitting || count === 0}
      type="button"
      onClick={submitForm}
      className="submit-button"
    >
      {`Send ${count} Email${count !== 1 ? 's' : ''}`}
    </Button>
  );
};

/**
 * Step 1's Next button gates on the live preview count from Formik context.
 * The default WizardForm `Footer` honours `showNextButton`, but this wizard
 * uses `renderFooterButtons` (for the dynamic submit caption), which bypasses
 * that mechanism — so we re-implement the zero-count gate locally to satisfy
 * the AC: "If filters result in zero clients, the 'Next' button should be disabled."
 */
const NextButton: React.FC<{ currentStep: number; onClick: () => void }> = ({ currentStep, onClick }) => {
  const { values } = useFormikContext<FormValues>();
  const previewCount = values.preview?.count ?? 0;
  const disabled = currentStep === 0 && previewCount === 0;
  return (
    <Button color="success" type="button" onClick={onClick} disabled={disabled} className="next-button">
      Next
    </Button>
  );
};

const BulkSignupWizard: React.FC<ComponentProps> = ({ isOpen, setIsOpen }) => {
  const [bulkSend] = useBulkSendMutation();
  const { showToast } = useToast();
  const dispatch = useAppDispatch();

  const handleSubmit = async (formData: FormValues): Promise<boolean> => {
    const { mandate_filter, has_email, has_outstanding_invoice, last_sent_days, subject, body } = formData;

    try {
      const result = await bulkSend({
        mandate_states: resolveMandateStates(mandate_filter),
        has_email,
        has_outstanding_invoice,
        last_sent_days,
        subject,
        body,
      }).unwrap();

      // Refresh the Client Signups table — its rows' last_sent_at / status values
      // now reflect the just-sent invites. clientSignupsApi owns the tag, so the
      // cross-slice invalidation must go through its util.
      dispatch(clientSignupsApi.util.invalidateTags([{ type: 'client-signups', id: 'LIST' }]));

      const sentMsg = `${result.sent_count} email(s) sent successfully.`;
      const failedMsg = result.failed_count > 0 ? ` ${result.failed_count} failed.` : '';
      showToast('success', `${sentMsg}${failedMsg}`);

      setIsOpen(false);
      return true;
    } catch (error) {
      showToast('danger', getErrorMessage(error));
      return false;
    }
  };

  return (
    <Modal
      size="lg"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header="Send Bulk Signup Links"
      includeFooter={false}
      unmountOnClose
    >
      <WizardForm<FormValues>
        steps={steps}
        initialValues={{
          mandate_filter: true,
          has_outstanding_invoice: false,
          has_email: true,
          subject: DEFAULT_SUBJECT,
          body: DEFAULT_BODY,
        }}
        onComplete={handleSubmit}
        renderFooterButtons={(handleNext, handlePrev, currentStep, stepCount) => {
          const isLastStep = currentStep === stepCount - 1;
          return (
            <div className="wizard-footer">
              <div className="footer-buttons-wrapper">
                <div className="d-flex">
                  {currentStep > 0 && (
                    <Button color="secondary" onClick={handlePrev} className="me-2">
                      Previous
                    </Button>
                  )}
                  {isLastStep ? <SubmitButton /> : <NextButton currentStep={currentStep} onClick={handleNext} />}
                </div>
              </div>
            </div>
          );
        }}
      />
    </Modal>
  );
};

export default BulkSignupWizard;
